import { ENV, Environment } from '@backoffice-frontend/environment'
import { useLocalStorage } from '@backoffice-frontend/hooks'

/**
 * The metropolis last picked in any TMP area of the Admin Panel. One key for all of them, so switching between
 * Vehicle Monitoring and Route Builder keeps the same metro. It lives in localStorage and is not one of the
 * user-specific keys `AuthContext` resets, so it survives a logout and a different user signing in on the machine.
 */
export const TMP_METROPOLIS_STORAGE_KEY = 'adminPanel_tmp_selectedMetropolisId'

/** West of this longitude a metropolis is in the Americas, east of it in Europe. */
const AMERICAS_MAX_LONGITUDE = -30

/**
 * The metropolises the TMP pickers offer in this environment: only US ones on the US production deployment, only
 * EU ones on the EU production deployment, and all of them elsewhere. Dev and int have no US deployment, so the US
 * test metros live there next to the EU ones.
 *
 * The API doesn't tell a metropolis' region, so it is read from its origin. A metropolis without an origin is kept.
 */
export const filterTmpMetropolisesByRegion = <T>(
  metropolises: ReadonlyArray<T>,
  getOrigin: (metropolis: T) => { longitude: number } | null | undefined,
  env: Environment = ENV,
): T[] => {
  const isUsDeployment = env === Environment['production.us-east-1']
  if (!isUsDeployment && env !== Environment.production) {
    return [...metropolises]
  }
  return metropolises.filter(metropolis => {
    const origin = getOrigin(metropolis)
    if (!origin) {
      return true
    }
    return origin.longitude < AMERICAS_MAX_LONGITUDE === isUsDeployment
  })
}

/** The metropolis assumed before the list has loaded and nothing was picked yet. */
const FALLBACK_METROPOLIS_ID = 1

/**
 * The metropolis to show: the stored one while it is still offered, otherwise the first one.
 *
 * Until the list has loaded the stored id is trusted as is, so the area doesn't fetch the first metro and then
 * jump to the stored one. A stored id the list no longer contains falls back instead of querying a dead metro.
 */
export const resolveTmpMetropolisId = (
  storedId: number | null,
  metropolises: ReadonlyArray<{ id: string | number }>,
): number => {
  const isOffered =
    metropolises.length === 0 ||
    metropolises.some(metropolis => Number(metropolis.id) === storedId)
  if (storedId !== null && isOffered) {
    return storedId
  }
  return Number(metropolises[0]?.id) || FALLBACK_METROPOLIS_ID
}

/** The TMP metropolis picker state, persisted on the machine across sessions and logouts. */
export const useTmpMetropolisId = (
  metropolises: ReadonlyArray<{ id: string | number }>,
): [number, (metropolisId: number) => void] => {
  const [storedId, setStoredId] = useLocalStorage<number | null>(
    TMP_METROPOLIS_STORAGE_KEY,
    null,
  )
  return [resolveTmpMetropolisId(storedId, metropolises), setStoredId]
}
